import WarningIcon from '@mui/icons-material/Warning';
import {
  Button,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import cloneDeep from 'lodash.clonedeep';

import { IGenerateSubMeterDto } from 'common/src/types/account.dtos';
import { IFacility, IMeter } from 'common/src/types/interfaces';

import GenericDialog from 'portal/src/components/common/GenericDialog';

const defaultSubMeters = (masterMeters): IGenerateSubMeterDto[] =>
  masterMeters
    .filter((m) => m.id && m.parentFacilityId)
    .map((m) => ({
      masterMeterId: m.id,
      parentFacilityId: m.parentFacilityId,
    }));

const GenerateSubAccountDialog = ({
  facilities,
  disabled,
  masterMeters,
  onCancel,
  onConfirm,
  open,
}: {
  facilities: Partial<IFacility>[];
  disabled: boolean;
  masterMeters: Partial<IMeter>[];
  onCancel: () => void;
  onConfirm: (subMeters: IGenerateSubMeterDto[]) => void;
  open: boolean;
}) => {
  const [subMeters, setSubMeters] = useState<IGenerateSubMeterDto[]>(() =>
    defaultSubMeters(masterMeters),
  );

  const handleFacilityChange = (
    masterMeterId: string,
    parentFacilityId: string,
  ) => {
    setSubMeters((prev) =>
      prev.map((m) =>
        m.masterMeterId === masterMeterId ? { ...m, parentFacilityId } : m,
      ),
    );
  };

  const handleClose = () => {
    setSubMeters(defaultSubMeters(masterMeters));
    onCancel();
  };

  const handleConfirm = () => {
    // clone the state before resetting the default
    const newSubMeters = cloneDeep(subMeters);
    onConfirm(newSubMeters);
    setSubMeters(defaultSubMeters(masterMeters));
  };

  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      title="Generate Sub Account"
    >
      <Stack spacing={2} sx={{ minWidth: 480 }}>
        <Typography variant="body1">
          Select the facility for each sub meter that will be created.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Master Meter Name</TableCell>
              <TableCell>Sub Meter Facility</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {masterMeters.map((meter) => (
              <TableRow key={meter.id}>
                <TableCell>{meter.name}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={
                      subMeters.find((s) => s.masterMeterId === meter.id)
                        ?.parentFacilityId ?? ''
                    }
                    onChange={(e) =>
                      handleFacilityChange(meter.id, e.target.value as string)
                    }
                    sx={{ minWidth: 220 }}
                  >
                    {facilities.map((facility) => (
                      <MenuItem key={facility.id} value={facility.id}>
                        {facility.name}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'center', mb: 2 }}>
          <WarningIcon sx={{ color: 'warning.main' }} />
          <Typography
            variant="subtitle1"
            color="warning.main"
            sx={{ alignSelf: 'center' }}
          >
            Once the sub account has been generated, you can view, edit, and
            delete the sub account on the "Accounts" tab. Sub meters can be
            viewed, edited, and deleted on the "Meters" tab.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button color="info" onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={disabled}
            disableElevation
            onClick={handleConfirm}
            variant="contained"
          >
            Generate
          </Button>
        </Stack>
      </Stack>
    </GenericDialog>
  );
};

export default GenerateSubAccountDialog;
